import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { JsonViewer } from '@dataxpdtn/mui-json-viewer';
import { existsFilter, toTitleCase } from '@iotv/core-services';
import { classDerivedDeviceEnabledTypes, DeviceClassName, flattenObject, GatewayClassName, getObjectHash, HourlyYearBasedNormalAccumulator, NetworkApplicationServerClassName, NodeEdgeMapper, } from '@iotv/datamodel';
import { AccumulatorClasses, isNonNaNNumber, isPosition, isValidBusinessObject, isValidGeoObject, Milliseconds, } from '@iotv/iotv-v3-types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Grid, Stack, Tab, Typography } from '@mui/material';
import objectHash from 'object-hash';
import { useContext, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { HistoryStoreContext } from '../../../../ContextProviders/HistoryStoreContext';
import { getNodeEdgeMapperReducer, NodeEdgeMapperContext } from '../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { getBoundsFromNodeEdgeMapper } from '../../../../GoogleMapV4/mapfns/translation';
import { ObservationCard } from '../../../../components/Observations_v2/ObservationCard';
import { EntityScoresConfig } from '../../../../components/Scoring';
import PhotosCard from '../../../../components/cards/PhotosCard';
import { IncidentListCard } from '../../../../components/common/IncidentListCard';
import { EntityReadingDataGrid } from '../../../../components/generic/EntityReadingDataGrid';
import { ObjectDataAccumulationCard } from '../../../../components/generic/ObjectDataAccumulationCard';
import ObjectGraphCard from '../../../../components/generic/ObjectGraphCard';
import ObjectHistoryStatistics from '../../../../components/generic/ObjectHistoryStatistics';
import { MapV4ShimView } from '../../../../components/geo/MapViewV4';
import config from '../../../../config';
import Assembler from '../../../../data/Assembler';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { extractPolylineFromHistoryStore, isDeviceEnabledType } from '../../../../data/TypeHelpers';
import { getDehydratedAccumulator } from '../../../../data/aws/s3/AccumulationPeriodization';
import { subscribeToCustomerThingTopic } from '../../../../data/daoFunctions/daoFunctions';
import { useMostProximateCustomer } from '../../../../hooks/useMostProximateCustomer';
import { useHistoryStore } from '../../../../hooks/useObjectHistory';
import { useTabHandlerStringKeys } from '../../../../hooks/useTabHandler';
import { ComponentMode, UserTransactionType } from '../../../../types/AppTypes';
import { ObjectAdjacentLinks } from './ObjectAdjacentLinks';
import { ObjectDetailCard } from './ObjectDetailCard';
import { ObjectHeader } from './ObjectHeader';
import { ObjectHistoryContext } from './ObjectHistoryContext';
const debug = process.env.REACT_APP_DEBUG && false;
const entityReadingsDefaults = config.app.webUI.entityTypeDefaults ?? {};
export default function ObjectCard(props) {
    'use no memo';
    debug && console.log('ObjectCard got props v2', props);
    const { viewObject, objectHistoryFilter: objectHistoryFilterIn, transactionFunctions, additionalTabs, assembleObject = false, applyUpdates = false, allowedActions, } = props;
    debug && console.log('ObjectCard can edit', allowedActions?.edit === true);
    const matchedPrimary = viewObject.matchedPrimary;
    const [{ applicationData }] = useContext(AppMainViewContext);
    const appDataHash = getObjectHash(applicationData);
    debug && console.log('ObjectCard appDataHash', appDataHash, applicationData?.nodeCount);
    const getContextItems = () => {
        let contextItems = [];
        if (applicationData) {
            const objectContext = applicationData.extractSingleNodeEdgeMapper(matchedPrimary);
            contextItems = objectContext.getContainedNodesAndEdges();
        }
        return contextItems;
    };
    const nodeEdgeReducer = getNodeEdgeMapperReducer({ canaryString: 'ObjectCard' });
    const [state, dispatch] = useReducer(nodeEdgeReducer, { nodeEdgeMapper: new NodeEdgeMapper(), nemHash: null, apexNode: null });
    useEffect(() => {
        if (applicationData) {
            const items = getContextItems();
            debug && console.log('ObjectCard useEffect appDataHash dispatching', appDataHash, items);
            dispatch({ type: 'loadItems', value: { items, canaryString: 'ObjectCard useEffect appDataHash' } });
        }
    }, [appDataHash]);
    if (matchedPrimary && isPosition(matchedPrimary?.location) && !matchedPrimary.points) {
        // eslint-disable-next-line react-hooks/immutability
        matchedPrimary.points = [matchedPrimary.location];
    }
    const entityReadingsEntry = entityReadingsDefaults?.[matchedPrimary?.type ?? 'default'] ?? entityReadingsDefaults?.['default'];
    const entityReadingLimitDays = entityReadingsEntry?.defaultEntityReadingsDays ?? 30;
    const [objectHistoryFilter] = useState(objectHistoryFilterIn ?? { recentStateHistory: { lastInNMs: entityReadingLimitDays * Milliseconds.DAY } });
    const useHistoryStoreOutput = useHistoryStore({
        contextObject: matchedPrimary,
        historyStoreFilter: objectHistoryFilter?.recentStateHistory ?? { lastInNMs: entityReadingLimitDays * Milliseconds.DAY },
    });
    const { objectHistory } = useHistoryStoreOutput;
    return (_jsx(ObjectHistoryContext.Provider, { value: { objectHistoryFilter, objectHistory }, children: _jsx(HistoryStoreContext.Provider, { value: useHistoryStoreOutput, children: _jsx(NodeEdgeMapperContext.Provider, { value: { dispatch, state }, children: _jsx(ObjectCardInternal, { viewObject, applicationData, transactionFunctions, additionalTabs, assembleObject, applyUpdates, allowedActions }, "ObjectCardInternal") }) }) }));
}
function ObjectCardInternal(props) {
    const { viewObject: viewObjectIn, applicationData, transactionFunctions, additionalTabs, assembleObject = false, applyUpdates = false, allowedActions, } = props;
    const params = useParams();
    const objectTypeIdFromParams = params.type;
    const [matchedPrimary, setMatchedPrimary] = useState(viewObjectIn.matchedPrimary);
    const viewObject = {
        ...viewObjectIn,
        matchedPrimary,
    };
    const assembleAndSet = (matchedPrimary) => {
        try {
            if (matchedPrimary && isValidBusinessObject(matchedPrimary)) {
                debug && console.log('ObjectCard assembling object', matchedPrimary);
                setMatchedPrimary(Assembler.getInstance(matchedPrimary));
            }
        }
        catch (e) {
            console.error('ObjectCard', 'Error assembling:', e);
        }
    };
    useEffect(() => {
        assembleAndSet(viewObjectIn.matchedPrimary);
    }, [objectHash(viewObjectIn.matchedPrimary ?? {})]);
    const mostProximateCustomer = useMostProximateCustomer({ item: matchedPrimary, query: true });
    const isDET = !!isDeviceEnabledType(matchedPrimary) || classDerivedDeviceEnabledTypes.includes(objectTypeIdFromParams ?? 'never');
    const [appContext] = useContext(AppMainViewContext);
    const { contextCustomer, contextUser, userGroupRoles, userSelectedGroup, roleArbitrationInstance, UserFunctions: userFunctions, isSmallView } = appContext;
    const { historyStore, historyStoreFns: { pushStateSnapShot }, objectHistory, loadingObjectHistory, } = useContext(HistoryStoreContext);
    const { state: { nodeEdgeMapper }, dispatch, } = useContext(NodeEdgeMapperContext);
    useEffect(() => {
        let subscription = undefined;
        if (mostProximateCustomer && contextUser) {
            const updateDataFromTopic = (data) => {
                debug && console.log(`ObjectCard received data `, data);
                const value = data.value;
                const flatObject = flattenObject(value);
                if (historyStore) {
                    const { timestamp, ...state } = Object.fromEntries(['timestamp', ...historyStore.keys()].map((k) => [k, flatObject[k]]));
                    const snapshot = { timestamp, state };
                    debug && console.log(`ObjectCard has snapshot `, snapshot);
                    if (isNonNaNNumber(timestamp))
                        pushStateSnapShot(snapshot);
                }
                else {
                    debug && console.log('ObjectCard has no history store ');
                }
                if (applyUpdates && isValidBusinessObject(matchedPrimary) && !('firmwareVersion' in value.state.reported)) {
                    const updated = structuredClone({ ...matchedPrimary, ...value.state.calculated });
                    debug && console.log('ObjectCard applying updated object', updated);
                    if (assembleObject)
                        assembleAndSet(updated);
                    else
                        setMatchedPrimary(updated);
                }
            };
            subscription = subscribeToCustomerThingTopic({
                customer: mostProximateCustomer,
                thing: matchedPrimary,
                user: contextUser,
                callback: updateDataFromTopic,
            });
        }
        else {
            debug && console.log(`ObjectCard not subscribing to topic`);
        }
        return () => {
            subscription?.unsubscribe();
        };
    }, [mostProximateCustomer?.sk, matchedPrimary?.sk, historyStore]);
    const [polylines, setPolylines] = useState({});
    const actions = [UserTransactionType.CREATE];
    const getEntityContext = async (entities) => {
        const { err, data } = await ConsumerDao.getEntityContext({ entities, includeDeviceConfigs: true, includeCustomerGroups: true });
        if (err) {
            console.log('ObjectCard getEntityContext error', err);
        }
        if (data) {
            const items = data.nodeEdgeMapper.getContainedNodesAndEdges();
            debug && console.log('ObjectCard getEntityContext data', data.nodeEdgeMapper.getSummary());
            const action = { type: 'loadItems', value: { items, canaryString: 'ObjectCard getEntityContext' } };
            dispatch(action);
            userFunctions.addObjectsToState(items);
        }
    };
    useEffect(() => {
        if (matchedPrimary && isDET)
            getEntityContext([matchedPrimary]);
    }, [isDET]);
    const derivedBounds = getBoundsFromNodeEdgeMapper({ nodeEdgeMapper });
    const derivedPosition = matchedPrimary?.location ?? derivedBounds?.getCenter();
    debug && console.log('ObjectCard geo derived position ', { derivedPosition, derivedBounds, nodeEdgeMapper });
    const [lastKnownPosition, setLastKnownPosition] = useState(null);
    useEffect(() => {
        if (matchedPrimary && historyStore) {
            const polyline = extractPolylineFromHistoryStore(historyStore, matchedPrimary?.sk ?? 'unknonw');
            setPolylines({ [matchedPrimary.sk]: polyline });
            debug && console.log(`ObjectCard polyline point count ${polyline.length}`);
            if (historyStore.lat && historyStore.lng) {
                const lats = [...historyStore.lat].reverse();
                const lngs = [...historyStore.lng].reverse();
                const pos = { lat: lats.find(existsFilter), lng: lngs.find(existsFilter) };
                if (isPosition(pos)) {
                    console.log(`ObjectCard useEffect set position`, pos);
                    setLastKnownPosition(pos);
                }
                else {
                    debug && console.log(`ObjectCard useEffect not position`, pos);
                }
            }
            else {
                debug && console.log(`ObjectCard useEffect objectHistory no lat lng keys`);
            }
        }
    }, [historyStore]);
    /** Fetch the accumulator, for use by the normals / scoring tabs */
    const [accumulator, setAccumulator] = useState(undefined);
    const [accumulatorError, setAccumulatorError] = useState(undefined);
    useEffect(() => {
        if (matchedPrimary && isDET && objectHistory)
            getDehydratedAccumulator(matchedPrimary, AccumulatorClasses.HourlyYearBasedNormalAccumulator).then(({ err, data }) => {
                if (err || !data) {
                    setAccumulatorError(err?.message ?? 'No data');
                }
                if (data) {
                    const accumulator = new HourlyYearBasedNormalAccumulator();
                    accumulator.ingest(data);
                    setAccumulator(accumulator);
                    debug && console.log('ObjectCard accumulator', accumulator);
                }
            });
    }, [matchedPrimary?.sk, isDET, objectHistory]);
    const DefaultTabs = {
        Details: () => matchedPrimary ? (_jsx(ObjectDetailCard, { matchedPrimary, mostProximateCustomer, transactionFunctions, userFunctions, allowedActions }, matchedPrimary.sk)) : null,
        Links: () => matchedPrimary ? (_jsx(ObjectAdjacentLinks, { viewObject, mostProximateCustomer, userGroupRoles, applicationData, userFunctions, transactionFunctions }, matchedPrimary.sk)) : null,
        Readings: matchedPrimary && isDET && objectHistory
            ? () => (_jsx(EntityReadingDataGrid, { context: { mostProximateCustomer, contextObject: matchedPrimary, historyStore: objectHistory.recentStateHistory }, seeExport: true }))
            : null,
        Statistics: matchedPrimary && isDET && objectHistory ? () => _jsx(ObjectHistoryStatistics, {}) : null,
        Observations: isDET && objectHistory
            ? () => _jsx(ObservationCard, { viewObject: viewObject, objectHistory: objectHistory, loadingObjectHistory: loadingObjectHistory }, 'ObservationsCard')
            : null,
        Charts: isDET ? () => _jsx(ObjectGraphCard, { historyObject: objectHistory?.recentStateHistory, isSmallView }) : null,
        Normals: isDET ? () => _jsx(ObjectDataAccumulationCard, { accumulator: accumulator, error: accumulatorError }) : null,
        Scores: matchedPrimary && isDET && objectHistory && userSelectedGroup
            ? () => (_jsx(EntityScoresConfig, { entity: matchedPrimary, objectHistory: objectHistory, userSelectedGroup: userSelectedGroup, isSmallView: isSmallView, userFunctions: userFunctions }))
            : null,
        Geo: matchedPrimary && (lastKnownPosition || derivedPosition || isValidGeoObject(matchedPrimary))
            ? () => (_jsx(Grid, { container: true, children: _jsx(Grid, { children: _jsx(MapV4ShimView, { contextObject: matchedPrimary, viewObjects: [viewObject], polylines, position: lastKnownPosition ?? derivedPosition }) }, 'GeoGrid') }, 'GeoParent'))
            : process.env.REACT_APP_IS_SUDO
                ? () => (_jsxs(Stack, { children: [_jsx(Typography, { children: "Not a valid GeoObject!" }), _jsx(JsonViewer, { value: matchedPrimary })] }))
                : null,
        /** where matched primary has no position, TODO, add a Positioning map, based on useGoogleMap which will allow 1 place, area, line to be defined and then saved as the correspoing ValidGeoObject attribute ( location, points, etc)  */
        // Geo: matchedPrimary  ? () => <GP container key={ 'GeoParent' }><GP item xs={ 12 } lg={ 12 } key={ 'GeoGrid' }>
        //   <MapV4ShimView { ...{ contextObject: matchedPrimary, locatableAddress, viewObjects: [ viewObject ], polylines, position: lastKnownPosition ?? derivedPosition } } />
        // </GP></GP> : process.env.REACT_APP_IS_SUDO ? () => <Stack><Typography>Not Valid GeoObject </Typography><JsonViewer { ...{ value: matchedPrimary } } /></Stack> : () => <></>,
        Incidents: isDET || [NetworkApplicationServerClassName, GatewayClassName, DeviceClassName].includes(matchedPrimary?.type ?? '')
            ? () => (_jsx(IncidentListCard, { viewObject,
                contextCustomer,
                user: contextUser,
                userGroupRoles,
                userSelectedGroup,
                roleArbitrationInstance,
                userFunctions,
                transactionFunctions,
                isSmallView }))
            : null,
        Media: matchedPrimary
            ? () => (_jsx(PhotosCard, { matchedPrimary,
                userFunctions,
                transactionFunctions,
                actions,
                title: `${matchedPrimary.name}'s photos`,
                /** ADH TODO: maybe should be only if the user owns this entity they can edit its photos */
                editMode: allowedActions?.edit === true ? ComponentMode.EDIT : ComponentMode.VIEW }))
            : null,
    };
    /** all the externally-defined tabs */
    const externalTabDefinitions = Object.fromEntries(Object.entries(additionalTabs ?? {}).map(([tabName, Komponent]) => [
        tabName,
        Komponent ? () => _jsx(Komponent, { ...appContext, viewObject, allowedActions }) : null,
    ]));
    /** tabs that are in addition to the base LSC tabs */
    const additionalTabDefinitions = Object.fromEntries(Object.entries(externalTabDefinitions).filter(([name]) => !Object.keys(DefaultTabs).includes(name)));
    /** tabs that will override base LSC tabs (including with `null`, thus effectively hiding / disabling them) */
    const overriddenTabDefinitions = Object.fromEntries(Object.entries(DefaultTabs).map(([name, Komponent]) => [name, externalTabDefinitions[name] !== undefined ? externalTabDefinitions[name] : Komponent]));
    const tabDefinitions = {
        ...additionalTabDefinitions,
        ...overriddenTabDefinitions,
    };
    const tabsToDisplay = Object.fromEntries(Object.entries(tabDefinitions)
        .filter(([name]) => {
        return (isDET ||
            [NetworkApplicationServerClassName, GatewayClassName, DeviceClassName].includes(matchedPrimary?.type ?? '') ||
            !['Readings', 'Statistics', 'Observations', 'Charts', 'Normals', 'Scores', 'Incidents'].includes(name));
    })
        .filter(([_, definition]) => definition !== null));
    const { state: [[tabValue, setTabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: matchedPrimary }), _jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay).map(([name], i) => (_jsx(Tab, { value: name, label: toTitleCase(name) }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay).map(([name, tab]) => (_jsx(TabPanel, { value: name, sx: { padding: 0, paddingBlockStart: 1 }, children: tab() }, `tabBase_${name}`)))] })] }));
}
